import Phaser from 'phaser';
import { networkManager, LobbyPlayer } from '../network/NetworkManager';
import { getAutoCreateCode, getAutoJoinCode } from '../utils/DebugConfig';

/**
 * LobbyScene - Room creation/joining for multiplayer (fully responsive)
 */
export class LobbyScene extends Phaser.Scene {
    // UI element references for repositioning on resize
    private titleText!: Phaser.GameObjects.Text;
    private createBtn!: Phaser.GameObjects.Rectangle;
    private createLabel!: Phaser.GameObjects.Text;
    private joinBtn!: Phaser.GameObjects.Rectangle;
    private joinLabel!: Phaser.GameObjects.Text;
    private roomCodeText!: Phaser.GameObjects.Text;
    private statusText!: Phaser.GameObjects.Text;
    private playersText!: Phaser.GameObjects.Text;
    private startBtn!: Phaser.GameObjects.Rectangle;
    private startText!: Phaser.GameObjects.Text;
    private backBtn!: Phaser.GameObjects.Text;

    private inputElement?: HTMLInputElement;
    private cancelButton?: HTMLButtonElement;
    private overlayClickHandler?: (e: MouseEvent) => void;
    private players: Map<string, LobbyPlayer> = new Map();

    constructor() {
        super({ key: 'LobbyScene' });
    }

    create(): void {
        // Title
        this.titleText = this.add.text(0, 0, '🎮 MULTIPLAYER LOBBY', {
            fontFamily: 'Arial Black, Arial',
            fontSize: '32px',
            color: '#4ecdc4',
            stroke: '#2a9d8f',
            strokeThickness: 4,
        }).setOrigin(0.5);

        // Create Room button
        this.createBtn = this.add.rectangle(0, 0, 200, 50, 0x6c5ce7);
        this.createBtn.setInteractive({ useHandCursor: true });
        this.createBtn.setStrokeStyle(2, 0x5849c4);

        this.createLabel = this.add.text(0, 0, 'CREATE ROOM', {
            fontFamily: 'Arial',
            fontSize: '18px',
            color: '#ffffff',
        }).setOrigin(0.5);

        this.createBtn.on('pointerover', () => this.createBtn.setFillStyle(0x7d6ee8));
        this.createBtn.on('pointerout', () => this.createBtn.setFillStyle(0x6c5ce7));
        this.createBtn.on('pointerup', () => this.createRoom());

        // Join Room button
        this.joinBtn = this.add.rectangle(0, 0, 200, 50, 0x00b894);
        this.joinBtn.setInteractive({ useHandCursor: true });
        this.joinBtn.setStrokeStyle(2, 0x00886a);

        this.joinLabel = this.add.text(0, 0, 'JOIN ROOM', {
            fontFamily: 'Arial',
            fontSize: '18px',
            color: '#ffffff',
        }).setOrigin(0.5);

        this.joinBtn.on('pointerover', () => this.joinBtn.setFillStyle(0x00d9a7));
        this.joinBtn.on('pointerout', () => this.joinBtn.setFillStyle(0x00b894));
        this.joinBtn.on('pointerup', () => this.showJoinDialog());

        // Room code display
        this.roomCodeText = this.add.text(0, 0, '', {
            fontFamily: 'Courier New, monospace',
            fontSize: '48px',
            color: '#fdcb6e',
            stroke: '#e17055',
            strokeThickness: 3,
        }).setOrigin(0.5);

        // Status text
        this.statusText = this.add.text(0, 0, '', {
            fontFamily: 'Arial',
            fontSize: '16px',
            color: '#888888',
        }).setOrigin(0.5);

        // Players list
        this.playersText = this.add.text(0, 0, '', {
            fontFamily: 'Arial',
            fontSize: '14px',
            color: '#ffffff',
            align: 'center',
        }).setOrigin(0.5);

        // Start Game button (hidden until ready)
        this.startBtn = this.add.rectangle(0, 0, 200, 50, 0xe17055);
        this.startBtn.setInteractive({ useHandCursor: true });
        this.startBtn.setStrokeStyle(2, 0xd63031);
        this.startBtn.setVisible(false);
        this.startBtn.setName('startBtn');

        this.startText = this.add.text(0, 0, 'START GAME', {
            fontFamily: 'Arial Black',
            fontSize: '20px',
            color: '#ffffff',
        }).setOrigin(0.5);
        this.startText.setVisible(false);
        this.startText.setName('startText');

        this.startBtn.on('pointerover', () => this.startBtn.setFillStyle(0xf0826d));
        this.startBtn.on('pointerout', () => this.startBtn.setFillStyle(0xe17055));
        this.startBtn.on('pointerup', () => this.startGame());

        // Back button
        this.backBtn = this.add.text(0, 0, '← Back to Menu', {
            fontFamily: 'Arial',
            fontSize: '16px',
            color: '#888888',
        });
        this.backBtn.setInteractive({ useHandCursor: true });
        this.backBtn.on('pointerover', () => this.backBtn.setColor('#cccccc'));
        this.backBtn.on('pointerout', () => this.backBtn.setColor('#888888'));
        this.backBtn.on('pointerup', () => {
            networkManager.disconnect();
            this.scene.start('MenuScene');
        });

        // Position all elements based on current viewport
        this.layoutUI();

        // Listen for resize events
        this.scale.on('resize', this.layoutUI, this);

        // Set up network callbacks
        this.setupNetworkCallbacks();

        // Check for debug auto-actions after a short delay to ensure assets are ready
        this.time.delayedCall(100, () => {
            const createCode = getAutoCreateCode();
            const joinCode = getAutoJoinCode();

            if (createCode) {
                this.createRoom(createCode);
            } else if (joinCode) {
                this.joinRoom(joinCode);
            }
        });
    }

    /**
     * Position all UI elements based on current viewport size
     */
    private layoutUI(): void {
        if (!this.cameras.main) return;

        const { width, height } = this.cameras.main;
        const centerX = width / 2;
        const centerY = height / 2;

        // Scale for smaller screens
        const isMobile = width < 500;
        const titleSize = isMobile ? '24px' : '32px';
        const buttonWidth = isMobile ? 140 : 200;
        const buttonHeight = isMobile ? 44 : 50;
        const buttonFontSize = isMobile ? '14px' : '18px';
        const buttonSpacing = isMobile ? 80 : 120;
        const codeSize = isMobile ? '36px' : '48px';

        // Title
        this.titleText.setPosition(centerX, isMobile ? 40 : 50);
        this.titleText.setFontSize(titleSize);

        // Create Room button
        this.createBtn.setPosition(centerX - (isMobile ? 75 : buttonSpacing), centerY - (isMobile ? 60 : 80));
        this.createBtn.setSize(buttonWidth, buttonHeight);
        this.createLabel.setPosition(this.createBtn.x, this.createBtn.y);
        this.createLabel.setFontSize(buttonFontSize);

        // Join Room button
        this.joinBtn.setPosition(centerX + (isMobile ? 75 : buttonSpacing), centerY - (isMobile ? 60 : 80));
        this.joinBtn.setSize(buttonWidth, buttonHeight);
        this.joinLabel.setPosition(this.joinBtn.x, this.joinBtn.y);
        this.joinLabel.setFontSize(buttonFontSize);

        // Room code
        this.roomCodeText.setPosition(centerX, centerY + (isMobile ? 10 : 20));
        this.roomCodeText.setFontSize(codeSize);

        // Status text
        this.statusText.setPosition(centerX, centerY + (isMobile ? 60 : 80));
        this.statusText.setFontSize(isMobile ? '14px' : '16px');

        // Players list
        this.playersText.setPosition(centerX, centerY + (isMobile ? 110 : 140));
        this.playersText.setFontSize(isMobile ? '12px' : '14px');

        // Start button
        this.startBtn.setPosition(centerX, centerY + (isMobile ? 170 : 220));
        this.startBtn.setSize(buttonWidth, buttonHeight);
        this.startText.setPosition(this.startBtn.x, this.startBtn.y);
        this.startText.setFontSize(isMobile ? '16px' : '20px');

        // Back button (bottom-left)
        this.backBtn.setPosition(20, height - 30);
        this.backBtn.setFontSize(isMobile ? '14px' : '16px');
    }

    private setupNetworkCallbacks(): void {
        networkManager.onPeerJoin = (peerId: string) => {
            this.players.set(peerId, {
                id: peerId,
                name: `Player ${this.players.size + 1}`,
                isReady: false,
                isHost: false,
            });
            this.updatePlayersDisplay();

            // Show start button for host when 2+ players
            if (networkManager.isHost && this.players.size >= 1) {
                this.showStartButton(true);

                // Broadcast current player list to all peers
                const playerList = Array.from(this.players.entries()).map(([id, p]) => ({
                    id,
                    name: p.name,
                    isReady: p.isReady,
                    isHost: p.isHost,
                }));
                networkManager.broadcast('LOBBY_PLAYER_LIST', playerList);
            }
        };

        networkManager.onPeerLeave = (peerId: string) => {
            this.players.delete(peerId);
            this.updatePlayersDisplay();

            if (networkManager.isHost && this.players.size < 1) {
                this.showStartButton(false);
            }
        };

        networkManager.subscribe('START_GAME', (message) => {
            this.transitionToGame(message.payload as { seed: number, width: number, height: number });
        });

        // Handle receiving player list from host
        networkManager.subscribe('LOBBY_PLAYER_LIST', (message) => {
            const playerList = message.payload as Array<{ id: string, name: string, isReady: boolean, isHost: boolean }>;

            // Update players map with received list
            for (const p of playerList) {
                // Don't overwrite our own entry
                if (p.id === networkManager.playerId) continue;

                this.players.set(p.id, {
                    id: p.id,
                    name: p.id === `fart-${this.roomCodeText?.text}` ? 'Host' : `Player ${p.name}`,
                    isReady: p.isReady,
                    isHost: p.isHost,
                });
            }
            this.updatePlayersDisplay();
        });

        networkManager.onError = (error: Error) => {
            this.statusText?.setText(`Error: ${error.message}`);
        };
    }

    private async createRoom(forcedCode?: string): Promise<void> {
        this.statusText?.setText('Creating room...');

        try {
            const code = await networkManager.createRoom('Host', forcedCode);
            this.roomCodeText?.setText(code);
            this.statusText?.setText('Room created! Share the code with friends.');

            // Add self to players
            this.players.set(networkManager.playerId, {
                id: networkManager.playerId,
                name: 'You (Host)',
                isReady: true,
                isHost: true,
            });
            this.updatePlayersDisplay();
        } catch (error) {
            this.statusText?.setText(`Failed to create room: ${error}`);
        }
    }

    private showJoinDialog(): void {
        // Create HTML input for room code
        if (this.inputElement) {
            this.dismissJoinDialog();
        }

        const isMobile = this.cameras.main.width < 500;
        const inputWidth = isMobile ? 300 : 440;

        this.inputElement = document.createElement('input');
        this.inputElement.type = 'text';
        this.inputElement.placeholder = 'ENTER ROOM CODE';
        this.inputElement.maxLength = 6;
        this.inputElement.style.cssText = `
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            font-size: 24px;
            font-family: 'Courier New', monospace;
            text-align: center;
            text-transform: uppercase;
            width: ${inputWidth}px;
            padding: 12px 0;
            border: 3px solid #00b894;
            border-radius: 8px;
            background: #2d3436;
            color: #ffffff;
            outline: none;
            z-index: 1000;
            box-shadow: 0 0 20px rgba(0,0,0,0.5);
        `;

        // Create cancel button
        this.cancelButton = document.createElement('button');
        this.cancelButton.textContent = 'CANCEL';
        this.cancelButton.style.cssText = `
            position: absolute;
            left: 50%;
            top: calc(50% + 65px);
            transform: translateX(-50%);
            font-size: 14px;
            font-weight: bold;
            width: ${inputWidth}px;
            padding: 10px 0;
            border: 2px solid #e74c3c;
            border-radius: 6px;
            background: #2d3436;
            color: #e74c3c;
            cursor: pointer;
            font-family: Arial, sans-serif;
            z-index: 1000;
            transition: all 0.2s;
            box-shadow: 0 4px 6px rgba(0,0,0,0.3);
        `;

        this.cancelButton.addEventListener('click', (e) => {
            e.stopPropagation();
            this.dismissJoinDialog();
        });

        this.cancelButton.addEventListener('mouseover', () => {
            this.cancelButton!.style.background = '#e74c3c';
            this.cancelButton!.style.color = '#fff';
        });

        this.cancelButton.addEventListener('mouseout', () => {
            this.cancelButton!.style.background = '#2d3436';
            this.cancelButton!.style.color = '#e74c3c';
        });

        this.inputElement.addEventListener('keydown', async (e) => {
            if (e.key === 'Enter' && this.inputElement) {
                const code = this.inputElement.value.toUpperCase();
                if (code.length === 6) {
                    await this.joinRoom(code);
                }
            }
            if (e.key === 'Escape') {
                this.dismissJoinDialog();
            }
        });

        document.body.appendChild(this.inputElement);
        document.body.appendChild(this.cancelButton);
        this.inputElement.focus();

        // Click outside to cancel
        this.overlayClickHandler = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target !== this.inputElement && target !== this.cancelButton) {
                this.dismissJoinDialog();
            }
        };

        // Add with slight delay to prevent immediate trigger from the button that opened it
        setTimeout(() => {
            document.addEventListener('click', this.overlayClickHandler!);
        }, 100);
    }

    private dismissJoinDialog(): void {
        if (this.inputElement) {
            this.inputElement.remove();
            this.inputElement = undefined;
        }

        if (this.cancelButton) {
            this.cancelButton.remove();
            this.cancelButton = undefined;
        }

        if (this.overlayClickHandler) {
            document.removeEventListener('click', this.overlayClickHandler);
            this.overlayClickHandler = undefined;
        }
    }

    private async joinRoom(code: string): Promise<void> {
        this.dismissJoinDialog();

        this.statusText?.setText(`Joining room ${code}...`);

        try {
            await networkManager.joinRoom(code);
            this.roomCodeText?.setText(code);
            this.statusText?.setText('Connected! Waiting for host to start...');

            // Add self to players
            this.players.set(networkManager.playerId, {
                id: networkManager.playerId,
                name: 'You',
                isReady: true,
                isHost: false,
            });

            // Add the host to players (we connected to them)
            const hostPeerId = `fart-${code.toUpperCase()}`;
            this.players.set(hostPeerId, {
                id: hostPeerId,
                name: 'Host',
                isReady: true,
                isHost: true,
            });

            this.updatePlayersDisplay();
        } catch (error) {
            this.statusText?.setText(`Failed to join: ${(error as Error).message}`);
        }
    }

    private updatePlayersDisplay(): void {
        const playerList = Array.from(this.players.values())
            .map(p => `${p.isHost ? '👑 ' : ''}${p.name}`)
            .join('\n');

        this.playersText?.setText(`Players (${this.players.size}):\n${playerList}`);
    }

    private showStartButton(show: boolean): void {
        this.startBtn?.setVisible(show);
        this.startText?.setVisible(show);
    }

    private startGame(): void {
        if (!networkManager.isHost) return;

        const seed = Date.now();

        // Calculate dimensions based on Host's screen
        // This makes the maze fit the host's screen but enforces that size on everyone else
        // so coordinates match perfectly.
        const width = this.cameras.main.width;
        const height = this.cameras.main.height;
        const mazeWidth = Math.floor(width / 32);
        const mazeHeight = Math.floor(height / 32);

        const config = {
            seed,
            width: mazeWidth,
            height: mazeHeight
        };

        // Broadcast start to all peers
        networkManager.broadcast('START_GAME', config);

        // Transition self
        this.transitionToGame(config);
    }

    private transitionToGame(data: { seed: number, width?: number, height?: number }): void {
        this.cameras.main.fadeOut(300);
        this.cameras.main.once('camerafadeoutcomplete', () => {
            this.scene.start('GameScene', {
                multiplayer: true,
                seed: data.seed,
                width: data.width || 25,
                height: data.height || 18,
                players: Array.from(this.players.keys()),
            });
        });
    }

    shutdown(): void {
        this.scale.off('resize', this.layoutUI, this);
        this.dismissJoinDialog();
    }
}
